import { useState, type KeyboardEvent } from 'react';
import { Lock, Plus, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { InputField } from '@/components/ui/InputField';
import { http } from '@/libs/http';
import {
  DEFAULT_SCOPES,
  RESERVED_PARAMS,
  isHttps,
  type DiscoveryResponse,
  type OAuthConfig,
} from './idp-oauth-config';

interface SectionProps {
  value: OAuthConfig;
  onChange: (value: OAuthConfig) => void;
}

const fieldClass =
  'w-full px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:border-(--primary) transition-colors';
const chip = 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border';

const errorOf = (e: unknown, fallback: string) => {
  const err = e as { response?: { data?: { message?: string } }; message?: string };
  return err.response?.data?.message ?? err.message ?? fallback;
};

// ─── Endpoints ───────────────────────────────────────────────────────────────

interface EndpointsProps extends SectionProps {
  isCustom: boolean;
  discovery: DiscoveryResponse | null;
  onDiscovered: (result: DiscoveryResponse) => void;
}

export const EndpointsSection = ({ value, onChange, isCustom, discovery, onDiscovered }: EndpointsProps) => {
  const [url, setUrl] = useState(value.discovery_url ?? '');
  const [loading, setLoading] = useState(false);

  const discover = async () => {
    setLoading(true);
    try {
      const result = await http.post<DiscoveryResponse>('/mgmt/v1/idps/discovery', { url });
      onChange({ ...value, ...result.config, discovery_url: result.config.discovery_url ?? url });
      onDiscovered(result);
      toast.success('Endpoints filled from the discovery document');
    } catch (err) {
      toast.error(errorOf(err, 'Discovery failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      title="Endpoints"
      subtitle={
        isCustom
          ? 'Where users sign in and where tokens are issued. Both endpoints are required.'
          : 'Okta endpoints are derived from the issuer URL. Override them only for a custom authorization server.'
      }
      className="glass"
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <label className="block text-sm font-bold text-slate-700 dark:text-slate-300" htmlFor="discovery-url">
            Discovery URL
          </label>
          <div className="flex gap-2">
            <input
              id="discovery-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && url.trim() && !loading && discover()}
              placeholder="https://idp.example.com  or  …/.well-known/openid-configuration"
              className={fieldClass}
            />
            <Button type="button" variant="secondary" onClick={discover} isLoading={loading} disabled={!url.trim()} className="gap-2 shrink-0">
              <Search className="w-4 h-4" /> Auto-fill
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Reads the provider&apos;s OpenID Connect discovery document and fills in the endpoints below.
          </p>
          {discovery?.warnings.map((w) => (
            <p key={w} className="text-xs text-amber-600 dark:text-amber-400">
              {w}
            </p>
          ))}
          {discovery && (
            <p className="text-xs text-slate-500">
              {discovery.refresh_hint
                ? 'This provider advertises refresh token support.'
                : 'This provider does not advertise refresh tokens, so the identity token may not be renewable.'}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InputField
            label={isCustom ? 'Authorization endpoint' : 'Authorization endpoint (override)'}
            placeholder="https://idp.example.com/oauth2/authorize"
            value={value.authorization_endpoint ?? ''}
            onChange={(e) => onChange({ ...value, authorization_endpoint: e.target.value || undefined })}
            error={isHttps(value.authorization_endpoint) ? undefined : 'Must be an https URL'}
            className="md:col-span-2"
          />
          <InputField
            label={isCustom ? 'Token endpoint' : 'Token endpoint (override)'}
            placeholder="https://idp.example.com/oauth2/token"
            value={value.token_endpoint ?? ''}
            onChange={(e) => onChange({ ...value, token_endpoint: e.target.value || undefined })}
            error={isHttps(value.token_endpoint) ? undefined : 'Must be an https URL'}
            helpText="Used to exchange the sign-in code and to renew tokens."
            className="md:col-span-2"
          />
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300" htmlFor="token-auth">
              Client authentication at the token endpoint
            </label>
            <select
              id="token-auth"
              value={value.token_auth_method}
              onChange={(e) => onChange({ ...value, token_auth_method: e.target.value as OAuthConfig['token_auth_method'] })}
              className={fieldClass}
            >
              <option value="client_secret_post">Secret in the request body (client_secret_post)</option>
              <option value="client_secret_basic">HTTP Basic header (client_secret_basic)</option>
            </select>
            {discovery && discovery.token_auth_methods_supported.length > 0 && (
              <p className="text-xs text-slate-500">Provider supports: {discovery.token_auth_methods_supported.join(', ')}</p>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

// ─── Scopes ──────────────────────────────────────────────────────────────────

export const ScopesSection = ({
  value,
  onChange,
  discovery,
}: SectionProps & { discovery: DiscoveryResponse | null }) => {
  const [input, setInput] = useState('');

  const add = (raw: string) => {
    const incoming = raw
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter((s) => s && !(DEFAULT_SCOPES as readonly string[]).includes(s) && !value.additional_scopes.includes(s));
    if (incoming.length) onChange({ ...value, additional_scopes: [...value.additional_scopes, ...new Set(incoming)] });
    setInput('');
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      add(input);
    } else if (e.key === 'Backspace' && !input && value.additional_scopes.length) {
      onChange({ ...value, additional_scopes: value.additional_scopes.slice(0, -1) });
    }
  };

  const suggestions = (discovery?.scopes_supported ?? []).filter(
    (s) => !(DEFAULT_SCOPES as readonly string[]).includes(s) && s !== 'offline_access' && !value.additional_scopes.includes(s),
  );

  return (
    <Card
      title="Scopes"
      subtitle="Permissions requested when users sign in. The default scopes are always included."
      className="glass"
    >
      <div className="space-y-5">
        <div className="space-y-2">
          <span className="block text-sm font-bold text-slate-700 dark:text-slate-300">Default</span>
          <div className="flex flex-wrap gap-2">
            {DEFAULT_SCOPES.map((scope) => (
              <span
                key={scope}
                className={`${chip} bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700`}
                title="Always requested; cannot be changed"
              >
                <Lock className="w-3 h-3" /> {scope}
              </span>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <span className="block text-sm font-bold text-slate-700 dark:text-slate-300">Additional</span>
          <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            {value.additional_scopes.map((scope) => (
              <span key={scope} className={`${chip} bg-sky-50 dark:bg-sky-950/30 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-900/50`}>
                {scope}
                <button
                  type="button"
                  aria-label={`Remove scope ${scope}`}
                  onClick={() => onChange({ ...value, additional_scopes: value.additional_scopes.filter((s) => s !== scope) })}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              onBlur={() => add(input)}
              placeholder={value.additional_scopes.length ? 'Add scope…' : 'Type a scope and press Enter'}
              aria-label="Add scope"
              className="flex-1 min-w-40 bg-transparent text-sm outline-none"
            />
          </div>
          <p className="text-xs text-slate-500">Separate several scopes with a space or comma.</p>
        </div>

        {suggestions.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-slate-400">Provider supports:</span>
            {suggestions.slice(0, 12).map((scope) => (
              <button
                key={scope}
                type="button"
                onClick={() => add(scope)}
                className={`${chip} border-dashed border-slate-300 dark:border-slate-600 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200`}
              >
                <Plus className="w-3 h-3" /> {scope}
              </button>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

// ─── Token renewal and extra parameters ──────────────────────────────────────

export const OptionsSection = ({ value, onChange }: SectionProps) => {
  const [key, setKey] = useState('');
  const [val, setVal] = useState('');

  const addParam = () => {
    const name = key.trim();
    if (!name) return;
    if (RESERVED_PARAMS.includes(name.toLowerCase()) || name.toLowerCase().startsWith('code_')) {
      toast.error(`"${name}" is set by the platform and cannot be overridden`);
      return;
    }
    onChange({ ...value, authorize_params: { ...value.authorize_params, [name]: val } });
    setKey('');
    setVal('');
  };

  const removeParam = (name: string) => {
    const next = { ...value.authorize_params };
    delete next[name];
    onChange({ ...value, authorize_params: next });
  };

  return (
    <>
      <Card title="Token renewal" subtitle="Keep the identity token fresh while a user is active." className="glass">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={value.offline_access}
            onChange={(e) => onChange({ ...value, offline_access: e.target.checked })}
            className="mt-1"
          />
          <span>
            <span className="block text-sm font-bold text-slate-700 dark:text-slate-300">Renew the identity token during sessions</span>
            <span className="block text-xs text-slate-500 mt-1">
              Requests <code>offline_access</code> so the provider returns a refresh token. If the provider rejects it, sign-in
              still works and the identity token just cannot be renewed. Providers that use another mechanism can be
              configured with an extra parameter below.
            </span>
          </span>
        </label>
      </Card>

      <Card
        title="Extra authorization parameters"
        subtitle="Added to the sign-in request, for provider-specific options such as access_type=offline."
        className="glass"
      >
        <div className="space-y-3">
          {Object.entries(value.authorize_params).length === 0 && (
            <p className="text-sm text-slate-500">No extra parameters.</p>
          )}
          {Object.entries(value.authorize_params).map(([name, v]) => (
            <div key={name} className="flex items-center gap-2 text-sm">
              <code className="px-2 py-1 rounded bg-slate-100 dark:bg-slate-800">{name}</code>
              <span className="text-slate-400">=</span>
              <code className="px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 flex-1 truncate">{v}</code>
              <Button type="button" variant="ghost" size="sm" aria-label={`Remove parameter ${name}`} onClick={() => removeParam(name)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <input value={key} onChange={(e) => setKey(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addParam()} placeholder="Name, e.g. access_type" aria-label="Parameter name" className={fieldClass} />
            <input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addParam()} placeholder="Value, e.g. offline" aria-label="Parameter value" className={fieldClass} />
            <Button type="button" variant="secondary" onClick={addParam} disabled={!key.trim()} className="shrink-0">
              Add
            </Button>
          </div>
        </div>
      </Card>
    </>
  );
};
